"use client";

import React, { useState } from "react";

import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import Button from "@/ui/Common/Button";
import { HeadTitle } from "@/ui/Common/HeadTitle";
import { Modal } from "@/ui/Modal";

import styles from "./style.module.scss";
import { useUserRole } from "@/hooks/useUserRole";
import FormTextarea from "@/ui/Forms/FormTextarea";

interface SimpleArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onArchive: (comment: string) => Promise<void>;
  propertyTitle?: string;
  mode: "archive" | "restore";
}

export function SimpleArchiveModal({
  isOpen,
  onClose,
  onArchive,
  propertyTitle,
  mode = "archive",
}: SimpleArchiveModalProps) {
  const t = useTranslations();
  const tObject = useTranslations("AddObjectForm");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comment, setComment] = useState<string>("");
  const { isAdmin, isSuperAdmin, isRealtor } = useUserRole();
  const hasAccess = isAdmin || isSuperAdmin || isRealtor;
  const isRestore = mode === "restore";

  const handleArchive = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onArchive(comment);
      window.location.reload();
      onClose();
    } catch (error) {
      console.error("Archive failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      classNames={{ backdrop: "z-modalOverlay", wrapper: "z-modal" }}
    >
      <div className={styles.wrapper}>
        <HeadTitle
          title={
            isRestore
              ? t("realtor.contextMenuRestoreText")
              : t("realtor.contextMenuArchiveText")
          }
        />

        {!isRestore && (
          <p className={styles.description}>
            {propertyTitle && (
              <span className={styles.propertyName}>{propertyTitle}</span>
            )}
            {t("realtor.actionsArchiveModalDescription")}
          </p>
        )}

        {!isRestore && hasAccess && (
          <FormTextarea
            label={t("realtor.actionsArchiveConfirmPrompt")}
            placeholder={tObject("Enter comments")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            error={""}
            required={true}
            rows={4}
          />
        )}

        <div className={styles.btnContainer}>
          <Button
            className="min-h-[56px]"
            type="gray"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("Cancel")}
          </Button>

          {!isRestore ? (
            <Button
              className="min-h-[56px]"
              onClick={handleArchive}
              disabled={isSubmitting || (hasAccess && comment.length < 2)}
              type="red"
            >
              {isSubmitting
                ? t("realtor.actionsArchiveLoading")
                : t("realtor.actionsArchiveText")}
            </Button>
          ) : (
            <Button
              className="min-h-[56px]"
              onClick={handleArchive}
              disabled={isSubmitting}
              type="red"
            >
              {isSubmitting
                ? t("realtor.actionsRestoreLoading")
                : t("realtor.actionsRestoreText")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
